import { useState, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Loader2, MoreHorizontal } from "lucide-react";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import { toast } from "sonner";

interface PresalePayment {
  _id: string;
  customerEmail?: string;
  amount: number;
  currency?: string;
  setupIntentId?: string;
  createdAt: string;
  purchasedProducts?: unknown[];
}

export function Presales() {
  const [payments, setPayments] = useState<PresalePayment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [chargingId, setChargingId] = useState<string | null>(null);
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  const loadPayments = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch("/api/payments/presales");
      if (!response.ok) {
        if (response.status === 401) {
          toast.error("You must be signed in to view presales.");
        } else {
          toast.error("Failed to load presales.");
        }
        setPayments([]);
        return;
      }
      const data = (await response.json()) as { payments?: PresalePayment[] };
      setPayments(
        (data.payments ?? []).filter(
          (p) => Array.isArray(p.purchasedProducts) && p.purchasedProducts.length > 0
        )
      );
    } catch {
      toast.error("Failed to load presales.");
      setPayments([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPayments();
  }, [loadPayments]);

  useEffect(() => {
    setBreadcrumbs([{ label: "Presales", path: "/presales" }]);
    return () => {
      clearBreadcrumbs();
    };
  }, [setBreadcrumbs, clearBreadcrumbs]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatAmount = (amount: number, currency?: string) => {
    const c = (currency || "usd").toLowerCase();
    const currencySymbol = c === "usd" ? "$" : "";
    return `${currencySymbol}${(amount / 100).toFixed(2)}`;
  };

  const handleCharge = async ({ paymentId }: { paymentId: string }) => {
    setChargingId(paymentId);
    try {
      const response = await fetch(`/api/payments/${paymentId}/charge`, {
        method: "POST",
      });
      const body = (await response.json().catch(() => ({}))) as { error?: string };
      if (!response.ok) {
        toast.error(
          typeof body.error === "string" ? body.error : "Charge failed."
        );
        return;
      }
      toast.success("Payment charged successfully.");
      setPayments((prev) => prev.filter((p) => p._id !== paymentId));
    } catch {
      toast.error("Charge failed.");
    } finally {
      setChargingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <h1 className="text-2xl font-semibold mb-4">Presales</h1>
      <Card>
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="w-[72px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.length > 0 ? (
                payments.map((payment) => (
                  <TableRow key={payment._id} className="hover:bg-muted/50">
                    <TableCell>{payment.customerEmail || "—"}</TableCell>
                    <TableCell>
                      {formatAmount(payment.amount, payment.currency)}
                    </TableCell>
                    <TableCell>{formatDate(payment.createdAt)}</TableCell>
                    <TableCell
                      className="p-2 text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            aria-label="Payment actions"
                            disabled={chargingId === payment._id}
                          >
                            {chargingId === payment._id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <MoreHorizontal className="h-4 w-4" />
                            )}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            disabled={
                              !payment.setupIntentId || chargingId === payment._id
                            }
                            onClick={() =>
                              handleCharge({ paymentId: payment._id })
                            }
                          >
                            Charge
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="h-24 text-center">
                    No uncaptured payments.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
